import { captureError } from '@smg-real-estate/browser-rum';
import type { Category, Listing } from '@smg-real-estate/hg-rets-schema';
import { cloneDeep, isEqual } from 'lodash-es';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  deleteInsertionImage,
  getInsertionById,
  patchInsertionStatus,
  postInsertionSubmittal,
  putFeatureFlags,
  putInsertion,
  putInsertionFurthestPage,
  putInsertionGuestInfo,
  putPublishAddress,
} from '@/api/insertions';
import { redirectDatatransTransaction } from '@/helpers/datatransUtils';
import { normalizeGuestUsernameInPlace } from '@/helpers/guestListing';
import { applyCategoryDependentRentDefaults } from '@/helpers/listingUtils';
import { saveSeonSession } from '@/helpers/seon';
import { applySharedApartmentListingDefaults } from '@/helpers/sharedApartment';
import { isIS24User } from '@/helpers/userUtils';
import { isHgRetsEmailFormat } from '@/helpers/validations';
import { Treatment } from '@/stores/featureFlags';
import {
  FeatureFlagName,
  InsertionStatus,
  PagesFlowOrder,
  PaymentType,
  type Insertion,
  type SelectedBundle,
} from '@/types';

export function mergeFeatureFlagsForInsertionUpdate(
  currentInsertion: Insertion,
  liveFlags: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const existing = currentInsertion.meta?.featureFlags ?? {};
  const flags = { ...liveFlags };

  if (
    existing[FeatureFlagName.NEW_LANDING_PAGE] === Treatment.ON &&
    flags[FeatureFlagName.NEW_LANDING_PAGE] === Treatment.OFF
  ) {
    flags[FeatureFlagName.NEW_LANDING_PAGE] = Treatment.ON;
  }

  return flags;
}

export interface FetchInsertionParams {
  insertionId: string;
  ownerId: string;
}

export interface SaveInsertionParams {
  ownerId: string;
}

// eslint-disable-next-line max-statements
export const useInsertionStore = defineStore('insertion', () => {
  const insertion = ref<Insertion>();
  // The following computed properties can safely be used to set the insertion ref's inner properties.
  // The default values are there to save on non-null assertions (!), but since the inputs can only be rendered
  // after the insertion is fetched, they will always point correctly to the insertion above.
  const listing = computed(() => insertion.value?.listing || {});
  const listingCategories = computed(() => listing.value.categories || []);
  const address = computed(() => listing.value.address || {});
  const lister = computed(() => listing.value.lister || {});
  const characteristics = computed(() => listing.value.characteristics || {});
  const billing = computed(() => lister.value.billing || {});
  const bundlePreselected = computed<SelectedBundle | undefined>(
    () => insertion.value?.selectedBundle,
  );
  const isNewLandingPageInsertion = computed(
    () =>
      !isIS24User() &&
      insertion.value?.meta?.featureFlags?.[FeatureFlagName.NEW_LANDING_PAGE] === Treatment.ON,
  );
  const needsListingCategory = computed(
    () => isNewLandingPageInsertion.value || listingCategories.value.length === 0,
  );

  const fetchError = ref<Error>();
  const isSaving = ref(false);
  const isSavingGuestEmail = ref(false);
  const saveError = ref<Error>();
  const isUpdatingHideAddress = ref(false);
  const isSubmitting = ref(false);
  const submitError = ref<Error>();
  const submitSuccess = ref(false);

  const selectedBillingContactId = ref<string | undefined>(undefined);

  const savedListingSnapshot = ref<Listing>();
  const hasSyncedListing = computed(() => savedListingSnapshot.value !== undefined);
  const isDirty = computed(
    () =>
      savedListingSnapshot.value !== undefined &&
      insertion.value?.listing !== undefined &&
      !isEqual(insertion.value.listing, savedListingSnapshot.value),
  );

  function captureListingSnapshot(listing?: Listing) {
    const source = listing ?? insertion.value?.listing;
    if (!source) return;

    savedListingSnapshot.value = cloneDeep(source);
  }

  function discardBillingChanges() {
    selectedBillingContactId.value = undefined;
    const listing = insertion.value?.listing;
    if (!listing || !savedListingSnapshot.value) return;

    const savedBilling = savedListingSnapshot.value.lister?.billing;
    if (savedBilling) {
      listing.lister = { ...listing.lister, billing: cloneDeep(savedBilling) };
    } else if (listing.lister) {
      delete listing.lister.billing;
    }
  }

  function setListingCategories(categories: Category[]) {
    if (!insertion.value?.listing) return;
    insertion.value.listing.categories = categories;
    applyCategoryDependentRentDefaults(insertion.value.listing, categories);
    applySharedApartmentListingDefaults(insertion.value.listing, categories);
  }

  async function fetchInsertion(params: FetchInsertionParams): Promise<void> {
    fetchError.value = undefined;
    try {
      const insertionData = await getInsertionById(params.ownerId, params.insertionId);

      //Cleanup localization urls
      if (insertionData?.listing?.localization?.de?.urls) {
        insertionData.listing.localization.de.urls =
          insertionData.listing.localization.de.urls.filter((url) => !!url);
      }

      insertion.value = insertionData;
    } catch (error) {
      fetchError.value = error instanceof Error ? error : new Error('Unknown error occurred');
      throw error;
    }
  }

  async function saveInsertion({ ownerId }: SaveInsertionParams): Promise<void> {
    if (!insertion.value) return;

    isSaving.value = true;
    saveError.value = undefined;

    try {
      const listingToSave = insertion.value.listing;
      // Snapshot before awaiting PUT so edits made while the request is in flight stay dirty.
      const sentSnapshot = cloneDeep(listingToSave);
      normalizeGuestUsernameInPlace(sentSnapshot);
      await putInsertion(ownerId, insertion.value.id, listingToSave);
      // Mirror the guest username promotion applied in putInsertion so later saves stay in sync with the API.
      normalizeGuestUsernameInPlace(listingToSave);
      savedListingSnapshot.value = sentSnapshot;
    } catch (error) {
      saveError.value = error instanceof Error ? error : new Error('Unknown error occurred');
      throw error;
    } finally {
      isSaving.value = false;
    }
  }

  async function updateHideAddress(shouldHideAddress: boolean) {
    if (!insertion.value || isUpdatingHideAddress.value) return;

    const userId = insertion.value.userId;
    const insertionId = insertion.value.id;
    const previousValue = insertion.value.shouldHideAddress;
    insertion.value.shouldHideAddress = shouldHideAddress;
    isUpdatingHideAddress.value = true;

    try {
      const updatedInsertion = await putPublishAddress(userId, insertionId, shouldHideAddress);
      if (insertion.value?.id === insertionId) {
        insertion.value.shouldHideAddress = updatedInsertion.shouldHideAddress;
      }
    } catch {
      if (insertion.value?.id === insertionId) {
        insertion.value.shouldHideAddress = previousValue;
      }
    } finally {
      isUpdatingHideAddress.value = false;
    }
  }

  let guestEmailSaveId = 0;

  /**
   * Persists `insertion.meta.guestInfo.accountEmail` via PUT …/guest-info (draft insertions).
   * Cleared values are updated locally only; empty strings are not sent (API rejects them).
   */
  async function saveGuestAccountEmail(accountEmail: string): Promise<void> {
    if (!insertion.value) return;

    const insertionId = insertion.value.id;
    const userId = insertion.value.userId;
    const saveId = ++guestEmailSaveId;

    isSavingGuestEmail.value = true;
    saveError.value = undefined;

    try {
      const updated = await putInsertionGuestInfo(userId, insertionId, {
        accountEmail,
      });
      if (insertion.value?.id === insertionId && saveId === guestEmailSaveId) {
        insertion.value.meta = updated.meta;
      }
    } catch (error) {
      saveError.value = error instanceof Error ? error : new Error('Unknown error occurred');
      captureError(error, {
        action: 'guest-account-email',
        insertionId,
      });
      throw error;
    } finally {
      if (saveId === guestEmailSaveId) {
        isSavingGuestEmail.value = false;
      }
    }
  }

  function shouldPersistGuestAccountEmail(email: string): boolean {
    return email.length > 0 && isHgRetsEmailFormat(email);
  }

  async function flushGuestAccountEmailSave(): Promise<void> {
    const email = insertion.value?.meta?.guestInfo?.accountEmail?.trim() ?? '';
    if (!shouldPersistGuestAccountEmail(email)) return;

    try {
      await saveGuestAccountEmail(email);
    } catch {
      // saveError is recorded in the insertion store
    }
  }

  async function updateFeatureFlags(featureFlags: Record<string, string | undefined>) {
    if (!insertion.value || insertion.value.status !== InsertionStatus.Draft) return;
    const insertionId = insertion.value.id;
    const userId = insertion.value.userId;
    const flags = mergeFeatureFlagsForInsertionUpdate(insertion.value, featureFlags);

    const updatedInsertion = await putFeatureFlags(userId, insertionId, flags);
    if (updatedInsertion?.meta && insertion.value?.id === insertionId) {
      insertion.value.meta = updatedInsertion.meta;
    }
  }
  // eslint-disable-next-line max-statements
  async function submitInsertion(paymentType?: PaymentType) {
    if (!insertion.value) return;

    isSubmitting.value = true;
    submitError.value = undefined;
    submitSuccess.value = false;

    await saveSeonSession({
      insertionId: insertion.value.id,
      ownerId: insertion.value.userId,
    });

    try {
      const { id: insertionId, userId: ownerId } = insertion.value;

      if (!hasSyncedListing.value || isDirty.value) {
        await putInsertion(ownerId, insertionId, insertion.value.listing);
      }

      // Unified submission flow (both invoice and direct)
      const submitResponse = await postInsertionSubmittal(ownerId, insertionId, paymentType);

      // Direct payment returns { transactionId }, otherwise returns Insertion
      const transactionId = (submitResponse as any)?.transactionId;

      if (!transactionId) {
        insertion.value = submitResponse as Insertion;
        captureListingSnapshot();
        submitSuccess.value = true;
        return;
      }

      // Not marking submitSuccess to true here; webhook will finalize
      await redirectDatatransTransaction(transactionId);
    } catch (error) {
      submitError.value = error instanceof Error ? error : new Error('Unknown error occurred');
    } finally {
      isSubmitting.value = false;
    }
  }

  async function changeStatus(status: InsertionStatus) {
    if (!insertion.value) return;

    try {
      isSaving.value = true;
      await patchInsertionStatus(insertion.value.userId, insertion.value.id, status);
      insertion.value.status = status;
    } catch {
      // The error is captured in the API layer, so we can fail silently here.
    } finally {
      isSaving.value = false;
    }
  }

  async function updateFurthestPage(furthestPage: PagesFlowOrder) {
    if (!insertion.value) return;

    /*
    FurthestPage is stored on the Insertion as a string, so to compare it must be converted back to the PagesFlowOrder enum.
    If the new value is a higher enum than the old value, the value is updated.
     */
    const currentFurthestPage = insertion.value.furthestPage;
    if (
      !currentFurthestPage ||
      furthestPage > PagesFlowOrder[currentFurthestPage as keyof typeof PagesFlowOrder]
    ) {
      const insertionId = insertion.value.id;

      await putInsertionFurthestPage(
        insertion.value.userId,
        insertion.value.id,
        PagesFlowOrder[furthestPage],
      );

      // User may have navigated away from the page at this point, e.g. in E2E tests.
      if (insertion.value?.id === insertionId) {
        insertion.value.furthestPage = PagesFlowOrder[furthestPage];
      }
    }
  }

  async function deleteImage(imageName: string) {
    if (!insertion.value) return;

    const userId = insertion.value.userId;
    const insertionId = insertion.value.id;

    try {
      await deleteInsertionImage(userId, insertionId, imageName);
    } catch {
      // The error is captured in the API layer, so we can fail silently here.
    }
  }

  function $reset() {
    insertion.value = undefined;
    fetchError.value = undefined;
    isSaving.value = false;
    isSavingGuestEmail.value = false;
    saveError.value = undefined;
    isUpdatingHideAddress.value = false;
    isSubmitting.value = false;
    submitError.value = undefined;
    submitSuccess.value = false;
    selectedBillingContactId.value = undefined;
    savedListingSnapshot.value = undefined;
  }

  return {
    insertion,
    selectedBillingContactId,
    listing,
    characteristics,
    listingCategories,
    address,
    lister,
    billing,
    bundlePreselected,
    isNewLandingPageInsertion,
    needsListingCategory,

    fetchInsertion,
    saveInsertion,
    captureListingSnapshot,
    discardBillingChanges,
    setListingCategories,
    updateHideAddress,
    saveGuestAccountEmail,
    flushGuestAccountEmailSave,
    submitInsertion,
    changeStatus,
    updateFurthestPage,
    deleteImage,
    updateFeatureFlags,

    fetchError,
    isSaving,
    isSavingGuestEmail,
    saveError,
    isUpdatingHideAddress,
    isSubmitting,
    submitError,
    submitSuccess,

    hasSyncedListing,
    isDirty,

    $reset,
  };
});
